import { logLogout } from "./session-logger";
import { clearLocalSession, logoutUser } from "./api";

/**
 * Comprehensive logout function that handles API call and cleanup
 * @param {Object} options - Logout options
 * @param {string} options.dbId - Database id header for AuthX logout
 * @param {string} options.redirectUrl - URL to redirect after logout (default: https://exacc.au/)
 * @param {boolean} options.forceCleanup - Force cleanup even if API fails (default: true)
 * @returns {Promise<boolean>} - True if logout completed (local cleanup always attempted)
 */
export const performLogout = async (options = {}) => {
  const {
    dbId,
    redirectUrl: _redirectUrl = "https://exacc.au/",
    forceCleanup = true,
  } = options;

  logLogout.attempt({ forceCleanup });
  let logoutSuccess = true;

  try {
    await logoutUser(dbId);
    logLogout.apiSuccess();
  } catch (error) {
    logLogout.apiFailed(error);
    logoutSuccess = false;
  }

  if (forceCleanup || logoutSuccess) {
    try {
      clearLocalSession();
      logLogout.cleanup();
    } catch (cleanupError) {
      logLogout.cleanupError(cleanupError);
    }
  }

  return logoutSuccess;
};

/**
 * Quick logout function that only performs local cleanup
 * Useful for client-side logout without API call
 */
export const performQuickLogout = () => {
  logLogout.attempt({ method: "quick" });

  try {
    clearLocalSession();
    logLogout.cleanup({ method: "quick" });
    return true;
  } catch (error) {
    logLogout.cleanupError(error, { method: "quick" });
    return false;
  }
};

/**
 * Logout and redirect function
 * @param {Object} options - Logout options (same as performLogout)
 * @param {number} options.delay - Delay before redirect in milliseconds (default: 1000)
 */
export const logoutAndRedirect = async (options = {}) => {
  const {
    delay = 1000,
    redirectUrl = "https://exacc.au/",
    ...logoutOptions
  } = options;

  const success = await performLogout(logoutOptions);

  setTimeout(() => {
    window.location.href = redirectUrl;
  }, delay);

  return success;
};

/**
 * Check if user should be logged out due to session expiration
 * @returns {boolean} - True if user should be logged out
 */
export const shouldLogoutUser = () => {
  return false;
};

/**
 * Handle logout with user feedback
 * @param {Function} showMessage - Function to show user messages
 * @param {Object} options - Logout options
 */
export const handleLogoutWithFeedback = async (showMessage, options = {}) => {
  try {
    const success = await performLogout(options);

    if (success) {
      showMessage("You have been logged out successfully", "success");
    } else {
      showMessage("Logout completed (with warnings)", "warning");
    }

    return success;
  } catch (error) {
    showMessage("Logout completed with errors", "error");
    return false;
  }
};
